import { useCallback, useEffect } from 'react';
import { useCrossTabCount } from 'hooks/useCrossTabCount';
import { useFeatureFlag } from 'hooks/useFeatureFlags';
import { EBroadcastChannel, EFeatureFlag } from 'constants/enums';

const REFRESH_COUNT_THRESHOLD = 5;

let hasIncremented = false;

interface UseStoreRefreshCountReturn {
  showPopup: boolean;
  count: number;
  onDismiss: () => void;
}

/**
 * Hook that tracks store-refresh page loads across browser tabs and
 * exposes popup state when the threshold is reached.
 *
 * A module-level guard ensures the counter increments once per page
 * load. The variable survives React unmount/remount during in-app
 * navigation but resets on a full page refresh.
 *
 * @example
 * const { showPopup, count, onDismiss } = useStoreRefreshCount();
 */
export const useStoreRefreshCount = (): UseStoreRefreshCountReturn => {
  const isEnabled = useFeatureFlag(EFeatureFlag.POPUP_STORE_REFRESH_ENABLED);
  const { count, increment, reset } = useCrossTabCount({
    channelName: EBroadcastChannel.STORE_REFRESH_CHANNEL_NAME,
    storageKey: EBroadcastChannel.STORE_REFRESH_STORAGE_KEY,
  });

  useEffect((): void => {
    if (!isEnabled || hasIncremented) return;

    hasIncremented = true;
    increment();
  }, [isEnabled, increment]);


  const onDismiss = useCallback((): void => {
    hasIncremented = false;
    reset();
  }, [reset]);

  const showPopup = Boolean(isEnabled) && count >= REFRESH_COUNT_THRESHOLD;

  
  return { showPopup, count, onDismiss };
};
